<template>
  <el-dialog
    :title="isEdit ? '编辑套餐' : '新增套餐'"
    :visible.sync="dialogVisible"
    width="600px"
    @close="handleClose"
  >
    <el-form
      ref="packageForm"
      :model="formData"
      :rules="rules"
      label-width="120px"
    >
      <!-- 所属商品 -->
      <el-form-item label="所属商品">
        <el-input v-model="productCode" disabled />
      </el-form-item>

      <!-- 套餐编码 -->
      <el-form-item label="套餐编码" prop="package_code">
        <el-input
          v-model="formData.package_code"
          placeholder="请输入套餐编码（如：HK05-10GB）"
          :disabled="isEdit"
          maxlength="50"
        />
        <div style="margin-top: 5px; font-size: 12px; color: #909399">
          建议格式：商品编码-规格，只能包含大写字母、数字和连字符
        </div>
      </el-form-item>

      <!-- 套餐名称 -->
      <el-form-item label="套餐名称" prop="package_name">
        <el-input
          v-model="formData.package_name"
          placeholder="请输入套餐名称"
          maxlength="50"
          show-word-limit
        />
      </el-form-item>

      <!-- 套餐价格 -->
      <el-form-item label="套餐价格" prop="price">
        <el-input-number
          v-model="formData.price"
          :precision="2"
          :step="1"
          :min="0.01"
          controls-position="right"
          style="width: 100%"
        />
        <div style="margin-top: 5px; font-size: 12px; color: #909399">
          单位：元
        </div>
      </el-form-item>

      <!-- 有效期 -->
      <el-form-item label="有效期" prop="duration">
        <el-input-number
          v-model="formData.duration"
          :step="1"
          :min="1"
          controls-position="right"
          style="width: 100%"
        />
        <div style="margin-top: 5px; font-size: 12px; color: #909399">
          单位：天
        </div>
      </el-form-item>

      <!-- 流量额度 -->
      <el-form-item label="流量额度" prop="data_amount">
        <el-input
          v-model="formData.data_amount"
          placeholder="请输入流量额度（如：10GB）"
          maxlength="20"
        />
        <div style="margin-top: 5px; font-size: 12px; color: #909399">
          格式：数字 + 单位（MB/GB/TB），如：10GB、500MB
        </div>
      </el-form-item>

      <!-- 套餐描述 -->
      <el-form-item label="套餐描述" prop="description">
        <el-input
          v-model="formData.description"
          type="textarea"
          :rows="4"
          placeholder="请输入套餐描述"
          maxlength="200"
          show-word-limit
        />
      </el-form-item>

      <!-- 收款商户 -->
      <el-form-item label="收款商户" prop="merchant_code">
        <el-select
          v-model="formData.merchant_code"
          placeholder="请选择收款商户（可选）"
          clearable
          filterable
          style="width: 100%"
          @change="handleMerchantChange"
        >
          <el-option
            v-for="item in merchantList"
            :key="item.itemValue"
            :label="item.itemLabel"
            :value="item.itemValue"
          >
            <span>{{ item.itemLabel }}</span>
            <span style="float: right; color: #8492a6; font-size: 12px">
              {{ item.itemValue }}
            </span>
          </el-option>
        </el-select>
        <div style="margin-top: 5px; font-size: 12px; color: #909399">
          不选择则使用商品的收款商户配置（优先级：套餐 > 商品 > 系统默认）
        </div>
      </el-form-item>

      <!-- 套餐状态（仅新增时显示） -->
      <el-form-item v-if="!isEdit" label="套餐状态" prop="status">
        <el-radio-group v-model="formData.status">
          <el-radio :label="1">上架</el-radio>
          <el-radio :label="0">下架</el-radio>
        </el-radio-group>
      </el-form-item>

      <el-alert
        v-if="isEdit"
        title="提示"
        type="warning"
        :closable="false"
        style="margin-top: 10px"
      >
        <div style="font-size: 12px; line-height: 1.8">
          1. 套餐编码创建后不可修改<br>
          2. 套餐状态请使用专用功能修改
        </div>
      </el-alert>
    </el-form>

    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" :loading="submitLoading" @click="handleSubmit">
        {{ isEdit ? '保存' : '创建' }}
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import { createPackage, updatePackage } from '@/api/iccid-package'
import { getDictData } from '@/api/dict'

export default {
  name: 'PackageDialog',
  props: {
    productCode: {
      type: String,
      required: true
    }
  },
  data() {
    // 套餐编码验证 - 已注释
    // const validatePackageCode = (rule, value, callback) => {
    //   if (!value) {
    //     callback(new Error('请输入套餐编码'))
    //   } else if (!/^[A-Z0-9-]+$/.test(value)) {
    //     callback(new Error('套餐编码只能包含大写字母、数字和连字符'))
    //   } else {
    //     callback()
    //   }
    // }

    const validatePrice = (rule, value, callback) => {
      if (value === null || value === undefined) {
        callback(new Error('请输入套餐价格'))
      } else if (value <= 0) {
        callback(new Error('套餐价格必须大于0'))
      } else {
        callback()
      }
    }

    const validateValidityDays = (rule, value, callback) => {
      if (value === null || value === undefined) {
        callback(new Error('请输入有效期'))
      } else if (value <= 0) {
        callback(new Error('有效期必须大于0'))
      } else if (!Number.isInteger(value)) {
        callback(new Error('有效期必须为整数'))
      } else {
        callback()
      }
    }

    // 流量额度验证 - 已注释
    // const validateDataAmount = (rule, value, callback) => {
    //   if (!value) {
    //     callback(new Error('请输入流量额度'))
    //   } else if (!/^\d+(\.\d+)?(MB|GB|TB)$/.test(value)) {
    //     callback(new Error('流量额度格式错误，如：10GB、500MB'))
    //   } else {
    //     callback()
    //   }
    // }

    return {
      dialogVisible: false,
      submitLoading: false,
      isEdit: false,
      merchantList: [],
      formData: {
        id: null,
        product_code: '',
        package_code: '',
        package_name: '',
        price: null,
        duration: null,
        data_amount: '',
        description: '',
        merchant_code: '',
        merchant_name: '',
        default_commission_type: 1,
        default_commission_value: 0,
        sort_order: 0,
        status: 1
      },
      rules: {
        package_code: [
          { required: true, message: '请输入套餐编码', trigger: 'blur' }
        ],
        package_name: [
          { required: true, message: '请输入套餐名称', trigger: 'blur' },
          { min: 2, max: 50, message: '套餐名称长度为2-50个字符', trigger: 'blur' }
        ],
        price: [
          { required: true, validator: validatePrice, trigger: 'blur' }
        ],
        duration: [
          { required: true, validator: validateValidityDays, trigger: 'blur' }
        ],
        data_amount: [
          { required: true, message: '请输入流量额度', trigger: 'blur' }
        ]
      }
    }
  },
  methods: {
    // 获取商户号列表
    async fetchMerchantList() {
      try {
        const { data } = await getDictData('FYPAY')
        this.merchantList = data || []
      } catch (error) {
        console.error('获取商户号列表失败:', error)
      }
    },

    // 商户选择变更
    handleMerchantChange(value) {
      if (value) {
        const selected = this.merchantList.find(item => item.itemValue === value)
        if (selected) {
          this.formData.merchant_name = selected.itemLabel
        }
      } else {
        // 清空选择
        this.formData.merchant_code = ''
        this.formData.merchant_name = ''
      }
    },

    // 打开弹窗
    async open(data) {
      this.dialogVisible = true
      this.isEdit = !!data

      // 获取商户号列表
      await this.fetchMerchantList()

      if (data) {
        // 编辑模式 - 保存完整数据以便提交时使用
        this.formData = {
          id: data.id,
          product_code: data.product_code || this.productCode,
          package_code: data.package_code,
          package_name: data.package_name,
          price: parseFloat(data.price),
          duration: parseInt(data.duration),
          data_amount: data.data_amount,
          description: data.description || '',
          merchant_code: data.merchant_code || '',
          merchant_name: data.merchant_name || '',
          default_commission_type: data.default_commission_type || 1,
          default_commission_value: data.default_commission_value || 0,
          sort_order: data.sort_order || 0,
          status: data.status
        }
      } else {
        // 新增模式
        this.formData = {
          id: null,
          product_code: this.productCode,
          package_code: '',
          package_name: '',
          price: null,
          duration: null,
          data_amount: '',
          description: '',
          merchant_code: '',
          merchant_name: '',
          default_commission_type: 1,
          default_commission_value: 0,
          sort_order: 0,
          status: 1
        }
      }
    },

    // 关闭弹窗
    handleClose() {
      this.dialogVisible = false
      this.$refs.packageForm.resetFields()
    },

    // 提交表单
    async handleSubmit() {
      this.$refs.packageForm.validate(async valid => {
        if (!valid) return

        this.submitLoading = true
        try {
          if (this.isEdit) {
            // 编辑套餐 - 传递所有必填字段

            const updateData = {
              id: this.formData.id,
              product_code: this.formData.product_code,
              package_code: this.formData.package_code,
              package_name: this.formData.package_name,
              price: this.formData.price,
              duration: this.formData.duration,
              data_amount: this.formData.data_amount,
              description: this.formData.description,
              merchant_code: this.formData.merchant_code || '',
              merchant_name: this.formData.merchant_name || '',
              default_commission_type: this.formData.default_commission_type,
              default_commission_value: this.formData.default_commission_value,
              sort_order: this.formData.sort_order,
              status: this.formData.status
            }

            await updatePackage(updateData)
            this.$message.success('套餐更新成功')
          } else {
            // 创建套餐
            await createPackage({
              product_code: this.productCode,
              package_code: this.formData.package_code,
              package_name: this.formData.package_name,
              price: this.formData.price,
              duration: this.formData.duration,
              data_amount: this.formData.data_amount,
              description: this.formData.description,
              merchant_code: this.formData.merchant_code || '',
              merchant_name: this.formData.merchant_name || '',
              status: this.formData.status
            })
            this.$message.success('套餐创建成功')
          }

          this.handleClose()
          this.$emit('success')
        } catch (error) {
          this.$message.error(error.message || (this.isEdit ? '更新失败' : '创建失败'))
        } finally {
          this.submitLoading = false
        }
      })
    }
  }
}
</script>

<style scoped>
.dialog-footer {
  text-align: right;
}
</style>
